import { createClient } from "@supabase/supabase-js";

import type { Database } from "@patient-watch/types";

const supabaseUrl: string = import.meta.env.VITE_SUPABASE_URL;
const supabaseKey: string = import.meta.env.VITE_SUPABASE_ANON_KEY;

const options = {
    auth: {
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: true,
    },
    realtime: {
        params: {
            eventsPerSecond: 10,
        },
    },
    timeout: 60000,
};

export const supabaseClient = createClient<Database>(supabaseUrl, supabaseKey, options);

// Public auth routes that handle their own unauthenticated state. We must not
// force a redirect to /login from these, otherwise a failed/expired invite link
// (which Supabase reports via detectSessionInUrl + a SIGNED_OUT event) bounces
// the user away from /confirm-account before they can request a new code.
const PUBLIC_AUTH_PATHS = [
    "/login",
    "/confirm-account",
    "/forgot-password",
    "/authenticate",
    "/set-password",
    "/change-password",
    "/new-user",
    "/signup",
    "/verify-email",
    "/magic",
];

// Listen for auth state changes across tabs.
// On a real sign-out we hard-redirect to /login to clear all in-memory state,
// but only when we're not already on a public auth page or handling an auth
// error link (Supabase puts those in the URL hash).
supabaseClient.auth.onAuthStateChange((event) => {
    if (event !== "SIGNED_OUT") return;

    localStorage.removeItem("tanstack.auth.id");

    const { pathname, hash } = window.location;

    // Supabase reports expired/invalid links in the hash; let the route handle it.
    const hashHasAuthError = hash.includes("error=") || hash.includes("error_code=");
    const onPublicAuthPage = PUBLIC_AUTH_PATHS.some((path) => pathname.startsWith(path));

    if (hashHasAuthError || onPublicAuthPage) {
        // 👀 Skipping forced /login redirect on public auth page / auth-error link
        console.log("[auth] SIGNED_OUT on public/auth-error page, not redirecting", {
            pathname,
            hashHasAuthError,
            onPublicAuthPage,
        });
        return;
    }

    window.location.href = "/login";
});

// Function to get the appropriate Supabase storage URL based on environment
export function getSupabaseStorageUrl(): string {
    const baseUrl = import.meta.env.VITE_SUPABASE_URL;
    if (!baseUrl) {
        console.error("VITE_SUPABASE_URL is not defined in environment variables.");
        return ""; // Return empty string or throw an error
    }
    // Ensure the URL ends with a trailing slash
    const url = baseUrl.endsWith("/") ? baseUrl : `${baseUrl}/`;
    return `${url}storage/v1/object/public/`;
}
